import React, { useEffect, useState } from 'react';

// Sticky in-page section nav with scroll-spy for long single-scroll forms.
// `sections` = [{ id, label }] where each id matches an element in the form.
// Highlights whichever section is currently in view and smooth-scrolls to one
// on click. Purely navigational — the form stays one scroll with one Save, so
// nothing is hidden. Hidden on narrow screens via CSS (.form-section-nav).
export default function SectionNav({ sections, topOffset = 88 }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!els.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Of the sections intersecting the viewport, the topmost one wins —
        // that's the section the reader is currently working in.
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView.length) setActiveId(inView[0].target.id);
      },
      // Bias the "active" band toward the top of the viewport so the highlight
      // tracks what you're reading, not what's scrolling in at the bottom.
      { rootMargin: `-${topOffset}px 0px -55% 0px`, threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections, topOffset]);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav className="form-section-nav" aria-label="Form sections">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => jumpTo(s.id)}
          className={`form-section-nav-item${activeId === s.id ? ' active' : ''}`}
          aria-current={activeId === s.id ? 'true' : undefined}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}
